import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { dateInput } from 'components/final-form/DateInput';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import { selectInput } from 'components/final-form/SelectInput';
import TextArea from 'components/final-form/TextArea';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, RadioProps } from 'semantic-ui-react';
import { getCompanyArticleGroupsForCompany } from 'services/companyServices';
import { getAllActiveDivisionsByCompany } from 'services/divisionServices';
import { searchEmployees } from 'services/employeeServices';
import { addStockOrder, editStockOrder } from 'services/stockOrderServices';
import { CompanyType, InterventionExternalCode, OrderStatus } from 'ts-types/api.enums';
import {
  ArticleGroupDto,
  CompanyArticleGroupDto,
  CompanyDto,
  DivisionDto,
  UpsertCompanyDto,
  UpsertStockOrderDto
} from 'ts-types/api.types';
import { IndexSignature } from 'utils/praxTypes';
import {
  composeValidators,
  isValueValidNumber,
  mustBeNumber,
  mustBePositiveNumber,
  mustNotBeZero,
  required
} from 'utils/validatorUtils';

interface Props extends AuthConsumerRenderProps, MessageSourceProps {

  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  stockOrderId?: number,
  stockOrder: Partial<UpsertStockOrderDto>,
  articleGroups: Array<ArticleGroupDto>,
  employeeId?: number,
  // shippableMaterials: Array<MedicamentDto>,
  companies: Array<CompanyDto>

}

interface State {

  status: Array<RadioProps>,

  articleGroupOptions: Array<DropdownOption>,
  // shippableMedicamentOptions: Array<DropdownOption>,
  companyOptions: Array<DropdownOption>,
  divisions: Array<DivisionDto>,
  divisionOptions: Array<DropdownOption>,
  employeeOptions: Array<DropdownOption>,
  errorMessage?: string,
  requiredDataLoaded: boolean,
  cancelTokenSource: CancelTokenSource

}

class UpsertStockOrderForm extends Component<Props, State> {

  private initialValues : Partial<UpsertStockOrderDto>;

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const statusLabels: Array<string> = Object.values(OrderStatus);

    const status = generateRadioButtonOptionsFromEnum(
        statusLabels,
        "enum.status",
        getMessage
    )

    this.state = {
      status: status,
      divisions: [],
      articleGroupOptions: [],
      // shippableMedicamentOptions: mapToDropdownOptionArray(
      //     props.shippableMaterials, ["code", "description"], "id", "id"),
      companyOptions: mapToDropdownOptionArray(
          props.companies, "description", "id", "id"),
      divisionOptions: [],
      employeeOptions: [],
      requiredDataLoaded: false,
      cancelTokenSource
    };

    const {stockOrderId, stockOrder, employeeId} = this.props;

    if (stockOrderId) {
      this.initialValues = {
        ...stockOrder
      };
    } else {
      this.initialValues = {
        ...stockOrder,
        employeeId: stockOrder.articleGroupId ? employeeId : undefined,
        status: 'ORDER',
        orderDate: moment().format("YYYY-MM-DD")
      };
    }
    /*
        getAllShippableMedicaments(cancelTokenSource)
            .then(response => {

              this.setState({
                shippableMedicamentOptions: mapToDropdownOptionArray(response, "description", "id", "id")
              });
            });
    */
  };

  componentDidMount() {
    this.loadRequiredData(this.state.cancelTokenSource);
  }

  loadRequiredData = async (cancelTokenSource: CancelTokenSource) => {
    const {currentUser} = this.props;
    try {
      const companyId = this.props.stockOrder.companyId;
      const mandateId = currentUser!.userConfig.mandateId;

      if (companyId) {
        let articleGroupOptions: Array<DropdownOption> = [];
        let companyArticleGroups: Array<CompanyArticleGroupDto> =
            await getCompanyArticleGroupsForCompany(companyId, cancelTokenSource)
        let filteredArticleGroups: Array<ArticleGroupDto> = [];
        if (companyArticleGroups.length > 0) {
          const articleGroupIds = companyArticleGroups.map(ag => ag.articleGroupId);
          filteredArticleGroups = this.props.articleGroups.filter(ag => articleGroupIds.includes(ag.id));
        }
        articleGroupOptions = mapToDropdownOptionArrayWithSep(
            filteredArticleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id")

        const searchValues = {companyId: companyId, mandateId: mandateId}
        let companyEmployees = await searchEmployees(searchValues, cancelTokenSource);
        companyEmployees = companyEmployees
            .filter(e => e.testType === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST.toString());
        const companyDivisions = await getAllActiveDivisionsByCompany(`${companyId}`, cancelTokenSource);

        this.setState({
          articleGroupOptions: articleGroupOptions,
          employeeOptions: mapToDropdownOptionArrayWithSep(
              companyEmployees, ["firstName", "name"], "id"),
          divisionOptions: mapToDropdownOptionArray(
              companyDivisions, "name", "id")
        });
      }
    } finally {
      this.setState({
        requiredDataLoaded: true
      });
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  handleSubmit = async (values: Partial<UpsertStockOrderDto>) => {
    const {stockOrderId, onSave} = this.props;
    const {cancelTokenSource} = this.state;


    const upsertStockOrderDto = {
      ...values
    };

    if (stockOrderId) {
      await editStockOrder(stockOrderId, upsertStockOrderDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addStockOrder(upsertStockOrderDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  onArticleGroupChange = async (articleGroupId: any, form: FormApi) => {
    if (articleGroupId === undefined || articleGroupId < 1 || articleGroupId === "") {
      form.change("employeeId", undefined)
    }
  }

  onCompanyChange = async (companyId: any, form: FormApi) => {

    const {articleGroups, currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;
    if (companyId === undefined || companyId < 1 || companyId === "") {
      form.change("employeeId", undefined)
      form.change("articleGroupId", undefined)
      form.change("divisionId", undefined);
    }

    if (companyId) {
      form.change("divisionId", undefined);
      form.change("articleGroupId", undefined);
      form.change("employeeId", undefined);
      const {cancelTokenSource} = this.state;
      const divisions = await getAllActiveDivisionsByCompany(companyId, cancelTokenSource);
      const companyArticleGroups = await getCompanyArticleGroupsForCompany(companyId, cancelTokenSource);
      const divisionOptions = mapToDropdownOptionArray(divisions, "name", "id", "id");
      let filteredArticleGroups: Array<ArticleGroupDto> = [];
      if (companyArticleGroups.length > 0) {
        const articleGroupIds = companyArticleGroups.map(ag => ag.articleGroupId);
        filteredArticleGroups = articleGroups.filter(ag => articleGroupIds.includes(ag.id));
      }
      const articleGroupOptions = mapToDropdownOptionArrayWithSep(
          filteredArticleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id")

      const searchValues = {companyId: companyId, mandateId: mandateId}
      let companyEmployees = await searchEmployees(searchValues, cancelTokenSource);
      companyEmployees = companyEmployees
          .filter(e => e.testType === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST.toString());

      const employeeOptions = mapToDropdownOptionArrayWithSep(
          companyEmployees, ["firstName", "name"], "id", "id")

      this.setState({
        divisionOptions: divisionOptions,
        articleGroupOptions: articleGroupOptions,
        employeeOptions: employeeOptions
      });

    }

  };

  validateOrderDate = (value: any, allValues: any) => {
    if (allValues.status === OrderStatus.ORDER) {
      if (value === undefined || value === "" || value === null) {
        return required(value);
      }
    }
  };

  validateDeliverDate = (value: any, allValues: any) => {
    if (allValues.status === OrderStatus.DELIVER) {
      if (value === undefined || value === "" || value === null) {
        return required(value);
      }
    }
  };

  validateEmployee = (value: any, allValues: any) => {
    if (allValues.articleGroupOptions === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST) {
      if (value === undefined || value === "" || value === null) {
        return true;
      }
    }
    return false;
  };

  resetCompanySubType = createDecorator({
    field: 'companyId',
    updates: {
      companySubType: (fieldValue, allValues?: Partial<UpsertCompanyDto>) => {

        return !fieldValue || !allValues || (fieldValue && fieldValue != CompanyType.SCHOOL)
            ? undefined
            : allValues.companySubType;
      }
  }
});

  render(): React.ReactNode {

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            decorators={[]}
            initialValues={this.initialValues}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}

        />
    );
  }

  renderForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {requiredDataLoaded, errorMessage} = this.state;

    if (requiredDataLoaded) {
      return (
          <form onSubmit={handleSubmit}>
            <FormContainer>
              <FormContent>
                {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
                }
                <PraxFormGrid>
                  {this.renderBasicInformationSection(values, form)}
                </PraxFormGrid>
              </FormContent>

              <FormButtonsRow>
                <Button compact type="submit" primary disabled={submitting}>
                  {getMessage("button.save")}
                </Button>
                <Button compact type="button" disabled={submitting}
                        onClick={onCancel}>{getMessage("button.cancel")}
                </Button>
              </FormButtonsRow>
            </FormContainer>
          </form>
      );
    }
  };

  renderBasicInformationSection = (values: any, form: FormApi): React.ReactNode => {

    const {articleGroupOptions, companyOptions, divisionOptions, employeeOptions, status} = this.state;
    const {articleGroups, getMessage} = this.props;

    let packageQuantity = 0;
    if (values.articleGroupId) {
      const articleGroup = articleGroups.find(a => a.id === values.articleGroupId);
      if (articleGroup) {
        packageQuantity = articleGroup.packageQuantity;
      }
    }

    let totalQuantity = 0;
    if (values.quantity && isValueValidNumber(values.quantity) && !(+values.quantity < 0)) {
      totalQuantity = values.quantity * packageQuantity;
    }

    let disableEmployeeSelect = true;
    if (values && values.companyId !== undefined && values.articleGroupId !== undefined) {
      const articleGroup = articleGroups.find(ag => ag.id === values.articleGroupId);
      if (articleGroup) {
        disableEmployeeSelect = articleGroup.externalCode !== InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST;
      }
    }

    const selectMap: IndexSignature = {
      articleGroupId: articleGroupOptions,
      // medicamentId: shippableMedicamentOptions,
      companyId: companyOptions,
      divisionId: divisionOptions,
      employeeId: employeeOptions
    };

    const basicLeftInfoFields = [
      selectInput({
            name: "companyId",
            clearable: true,
            customOnChange: (value: any) => this.onCompanyChange(value, form)
          },
          selectMap
      ),
      selectInput({
            name: "divisionId",
            clearable: true,
            disabled: !form.getState().values.companyId
          },
          selectMap
      ),
      dateInput({
        name: "orderDate",
        validate: this.validateOrderDate,
      }),
      textInput({
        name: "quantity",
        validate: composeValidators(required, mustBeNumber, mustNotBeZero, mustBePositiveNumber),
      })
    ];

    const basicRightInfoFields = [
      selectInput({
            name: "articleGroupId",
            clearable: true,
            disabled: !form.getState().values.companyId,
            customOnChange: (value: any) => this.onArticleGroupChange(value, form)
          },
          selectMap
      ),
      // selectInput({
      //       name: "medicamentId",
      //       clearable: true,
      //       disabled: true /*!!form.getState().values.articleGroupId*/
      //     },
      //     selectMap
      // ),
      selectInput({
            name: "employeeId",
            clearable: true,
            disabled: disableEmployeeSelect,
            // required: values.articleGroupId ? true : false
            // TODO AB make employee required if AG pcr individual is selected
          },
          selectMap
      ),
      dateInput({
        name: "deliverDate",
        validate: this.validateDeliverDate
      })
    ];

    const mappedLeftRow = basicLeftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage(`stockOrder.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = basicRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={4} verticalAlign="middle">
              {`${getMessage(`stockOrder.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={12} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={12} verticalAlign="middle" textAlign="left">
              <Field
                  name="status"
                  component={RadioGroup}
                  radioDefinitions={status}
                  fluid
                  validate={required}
                  radiowidth="20%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
                <Grid.Row>
                  <Grid.Column width={10}>
                    {getMessage('stockOrder.packageQuantity')}:
                  </Grid.Column>
                  <Grid.Column width={6} textAlign={'right'}>
                    &nbsp;<strong>{packageQuantity}</strong>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10}>
                    {getMessage('stockOrder.totalQuantity')}:
                  </Grid.Column>
                  <Grid.Column width={6} textAlign={'right'}>
                    &nbsp;<strong>{totalQuantity}</strong>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row></Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Field
                  name="remarks"
                  component={TextArea}
                  rows={3}
                  placeholder={getMessage("remarks")}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withAuthContext(withMessages(UpsertStockOrderForm))
